import { useEffect, useState, type FormEvent } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Money as Cash,
  Bank,
  PiggyBank,
  Sparkle,
  CaretLeft,
  ArrowsLeftRight,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'
import type {
  Caja,
  CuentaAhorroCorriente,
  CuentaAportaciones,
  CuentaBancariaPropia,
  FormaPago,
  FormaPagoPayload,
  OperacionAsociadoResult,
  ReferenciaBancariaAsociado,
  TipoOperacionCuenta,
} from '@/lib/typesNeoCore'
import { imprimirReciboIngresos, imprimirReciboEgresos } from '@/lib/imprimibles'
import { DrawerShell } from './DrawerShell'
import { hoyGT } from '../lib/fechas'

/**
 * Drawer de "Nueva operación" desde la ficha del asociado.
 *
 * 2 pasos:
 *   1. Selector visual del tipo de operación (depósito ahorro / depósito
 *      aportaciones / retiro)
 *   2. Form con monto + cuenta destino (si hay opciones) + formas de pago
 *
 * Llama RPC `registrar_operacion_asociado` que actualiza saldo + crea
 * movimiento + asigna correlativo INGECOP RIG/REG en una transacción.
 *
 * Al éxito: muestra confirmación + botón para imprimir comprobante (RIG)
 * o descargar imagen.
 */

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  nombreCompleto: string
  noAsociado: string
  neoId?: string
  dpi?: string
  referenciaBancaria?: ReferenciaBancariaAsociado | null
  onSuccess: () => void
}

// FormaPago para esta UI: efectivo, depósito (solo INGRESO), transferencia bancaria (solo EGRESO)
type FormaUI = Extract<FormaPago, 'efectivo' | 'deposito' | 'transferencia_bancaria'>

type TipoOp = {
  codigo: TipoOperacionCuenta
  label: string
  hint: string
  Icon: typeof Cash
  direccion: 'in' | 'out'
}

const TIPOS: TipoOp[] = [
  {
    codigo: 'E13',
    label: 'Depósito a ahorro',
    hint: 'El asociado deposita en su cuenta de ahorro corriente.',
    Icon: PiggyBank,
    direccion: 'in',
  },
  {
    codigo: 'E28',
    label: 'Depósito a aportaciones',
    hint: 'El asociado aumenta su capital aportado a la cooperativa.',
    Icon: Sparkle,
    direccion: 'in',
  },
  {
    codigo: 'S4',
    label: 'Retiro',
    hint: 'El asociado retira de su ahorro o aportaciones.',
    Icon: ArrowUp,
    direccion: 'out',
  },
]

export function NuevaOperacionDrawer({
  open, onClose, asociadoId, nombreCompleto, noAsociado, neoId, dpi,
  referenciaBancaria, onSuccess,
}: Props) {
  // Catálogos
  const [caja, setCaja] = useState<Caja | null>(null)
  const [cuentasBanco, setCuentasBanco] = useState<CuentaBancariaPropia[]>([])
  const [ctaAportaciones, setCtaAportaciones] = useState<CuentaAportaciones | null>(null)
  const [ctasAhorro, setCtasAhorro] = useState<CuentaAhorroCorriente[]>([])
  const [loadingCat, setLoadingCat] = useState(true)
  const [errorCat, setErrorCat] = useState<string | null>(null)

  // UI state
  const [tipoSel, setTipoSel] = useState<TipoOp | null>(null)
  const [montoStr, setMontoStr] = useState<string>('')
  const [cuentaDestinoId, setCuentaDestinoId] = useState<string>('')
  const [formaPago, setFormaPago] = useState<FormaUI>('efectivo')
  const [descripcion, setDescripcion] = useState<string>('')

  // Datos del depósito si forma_pago=deposito
  const [cuentaBancariaId, setCuentaBancariaId] = useState<string>('')
  const [fechaBoleta, setFechaBoleta] = useState<string>(hoyGT())
  const [numeroBoleta, setNumeroBoleta] = useState<string>('')
  const [montoBoletaTotal, setMontoBoletaTotal] = useState<string>('')

  // Submit
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<OperacionAsociadoResult | null>(null)

  const monto = Number(montoStr) || 0

  // Cargar catálogos al abrir
  useEffect(() => {
    if (!open) return

    setLoadingCat(true)
    setErrorCat(null)
    setSuccess(null)
    setError(null)
    setTipoSel(null)
    setMontoStr('')
    setCuentaDestinoId('')
    setFormaPago('efectivo')
    setDescripcion('')
    setNumeroBoleta('')
    setMontoBoletaTotal('')

    Promise.all([
      supabase.from('caja').select('*').eq('activa', true).eq('tipo', 'FISICA').limit(1).single(),
      supabase.from('cuenta_bancaria_propia').select('*').eq('activa', true).order('alias', { ascending: true }),
      supabase.from('cuenta_aportaciones').select('*').eq('asociado_id', asociadoId).eq('estado', 'activa').maybeSingle(),
      supabase.from('cuenta_ahorro_corriente').select('*').eq('asociado_id', asociadoId).eq('estado', 'activa').order('abierta_at'),
    ]).then(([cajaResp, ctasBancoResp, ctaApoResp, ctasAhoResp]) => {
      if (cajaResp.error) { setErrorCat('No hay caja física activa.'); setLoadingCat(false); return }
      setCaja(cajaResp.data as Caja)
      setCuentasBanco((ctasBancoResp.data ?? []) as CuentaBancariaPropia[])
      setCtaAportaciones((ctaApoResp.data as CuentaAportaciones | null) ?? null)
      setCtasAhorro((ctasAhoResp.data ?? []) as CuentaAhorroCorriente[])
      setLoadingCat(false)
    })
  }, [open, asociadoId])

  // Cuando elige un tipo, pre-popular cuenta_destino_id con la opción única (si solo hay una)
  useEffect(() => {
    if (!tipoSel) { setCuentaDestinoId(''); return }
    if (tipoSel.codigo === 'E13') {
      // depósito ahorro: si solo hay 1 ahorro, lo usa
      if (ctasAhorro.length === 1) setCuentaDestinoId(ctasAhorro[0].id)
      else setCuentaDestinoId('')
    } else if (tipoSel.codigo === 'E28') {
      // depósito aportaciones: 1:1 siempre
      if (ctaAportaciones) setCuentaDestinoId(ctaAportaciones.id)
    } else if (tipoSel.codigo === 'S4') {
      // retiro: el operador elige
      setCuentaDestinoId('')
    }
  }, [tipoSel, ctasAhorro, ctaAportaciones])

  const cuentasDisponibles = (() => {
    if (!tipoSel) return []
    if (tipoSel.codigo === 'E13') {
      return ctasAhorro.map((c) => ({
        id: c.id,
        label: `${c.alias ?? 'Ahorro'} · ${c.numero_cuenta}`,
        saldo: c.saldo,
      }))
    }
    if (tipoSel.codigo === 'E28' && ctaAportaciones) {
      return [{
        id: ctaAportaciones.id,
        label: `Aportaciones · ${ctaAportaciones.numero_cuenta}`,
        saldo: ctaAportaciones.saldo,
      }]
    }
    if (tipoSel.codigo === 'S4') {
      const items: Array<{ id: string; label: string; saldo: number }> = []
      if (ctaAportaciones) items.push({
        id: ctaAportaciones.id,
        label: `Aportaciones · ${ctaAportaciones.numero_cuenta}`,
        saldo: ctaAportaciones.saldo,
      })
      ctasAhorro.forEach((c) => items.push({
        id: c.id,
        label: `${c.alias ?? 'Ahorro'} · ${c.numero_cuenta}`,
        saldo: c.saldo,
      }))
      return items
    }
    return []
  })()

  const cuentaSel = cuentasDisponibles.find((c) => c.id === cuentaDestinoId)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!tipoSel || !caja) return

    if (monto <= 0) { setError('El monto debe ser mayor a 0.'); return }
    if (!cuentaDestinoId) { setError('Seleccioná la cuenta destino.'); return }

    if (tipoSel.codigo === 'S4' && cuentaSel && monto > cuentaSel.saldo) {
      setError(`Saldo insuficiente. Disponible: Q ${cuentaSel.saldo.toFixed(2)}`)
      return
    }

    // Construir array pagos
    const pagos: FormaPagoPayload[] = []
    if (formaPago === 'efectivo') {
      pagos.push({ tipo: 'efectivo', monto })
    } else if (formaPago === 'deposito') {
      // Solo aplica para INGRESOS (depósitos del asociado a su cuenta vía banco propio)
      if (!cuentaBancariaId) { setError('Seleccioná la cuenta bancaria.'); return }
      if (!numeroBoleta.trim()) { setError('Falta el N° de boleta.'); return }
      const totalBoleta = Number(montoBoletaTotal)
      if (!totalBoleta || totalBoleta < monto) {
        setError(`El monto total de la boleta no puede ser menor al monto a registrar.`)
        return
      }
      pagos.push({
        tipo: 'deposito',
        monto,
        cuenta_bancaria_propia_id: cuentaBancariaId,
        fecha_boleta: fechaBoleta,
        numero_boleta: numeroBoleta.trim(),
        monto_boleta_total: totalBoleta,
      })
    } else if (formaPago === 'transferencia_bancaria') {
      // Solo aplica para EGRESOS (la coop transfiere al banco del asociado)
      if (!referenciaBancaria) {
        setError('El asociado no tiene cuenta bancaria registrada.')
        return
      }
      pagos.push({
        tipo: 'transferencia_bancaria',
        monto,
        referencia_bancaria_destino_id: referenciaBancaria.id,
      })
    }

    setSubmitting(true)
    const { data, error: rpcErr } = await supabase.rpc('registrar_operacion_asociado', {
      p_input: {
        asociado_id: asociadoId,
        caja_id: caja.id,
        tipo_movimiento_codigo: tipoSel.codigo,
        monto,
        descripcion: descripcion.trim() || null,
        pagos,
        cuenta_destino_id: cuentaDestinoId,
      },
    })
    setSubmitting(false)

    if (rpcErr) { setError(`No se pudo registrar: ${rpcErr.message}`); return }

    setSuccess(data as OperacionAsociadoResult)
    onSuccess()
  }

  if (!open) return null

  // Vista de éxito
  if (success && tipoSel) {
    const isIngreso = tipoSel.direccion === 'in'

    const onReimprimir = async () => {
      // Fetch del movimiento para sacar serie/correlativo + verification_code para el QR
      const { data: mov } = await supabase
        .from('movimiento_caja')
        .select(`
          correlativo, fecha, hora, verification_code,
          serie:serie_documento_id (codigo)
        `)
        .eq('id', success.movimiento_caja_id)
        .single()

      if (!mov) return

      const sessionData = (await supabase.auth.getSession()).data.session
      const cajero = sessionData?.user?.email ?? 'Operador'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const m = mov as any

      // Si es retiro por transferencia bancaria, mostrar la cuenta destino
      // del asociado en el comprobante (vino como prop al drawer).
      const esRetiroTransferencia = !isIngreso && formaPago === 'transferencia_bancaria'
      const banco = esRetiroTransferencia ? referenciaBancaria?.banco : undefined
      const numeroCuenta = esRetiroTransferencia ? referenciaBancaria?.numero_cuenta : undefined

      const reciboData = {
        serie: m.serie?.codigo ?? '',
        correlativo: m.correlativo,
        fecha: `${m.fecha}T${m.hora}`,
        asociado_no: noAsociado,
        neo_id: neoId,
        asociado_nombre: nombreCompleto,
        asociado_dpi: dpi ?? '',
        concepto: tipoSel.label,
        monto,
        forma_pago: formaPago,
        cajero,
        banco,
        numero_cuenta: numeroCuenta,
        verification_code: m.verification_code,
      }

      if (isIngreso) {
        imprimirReciboIngresos(reciboData)
      } else {
        imprimirReciboEgresos(reciboData)
      }
    }

    return (
      <DrawerShell onClose={onClose}>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: isIngreso ? 'var(--ok-bg)' : '#fee2e2',
              color: isIngreso ? 'var(--ok-ink)' : '#dc2626',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isIngreso ? <ArrowDown size={20} weight="bold" /> : <ArrowUp size={20} weight="bold" />}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Operación registrada
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', marginTop: 2 }}>
                {tipoSel.label}
              </div>
            </div>
          </div>

          <div style={{
            padding: '16px 18px', background: 'var(--soft-gray)', border: '1px solid var(--hairline)',
            borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <RowMini label="Monto" value={`${isIngreso ? '+' : '−'}${formatQ(monto)}`} accent={isIngreso ? 'var(--ok-ink)' : '#dc2626'} bold />
            <RowMini label="Saldo anterior" value={`Q ${success.saldo_anterior.toFixed(2)}`} />
            <RowMini label="Saldo nuevo" value={`Q ${success.saldo_nuevo.toFixed(2)}`} bold />
          </div>

          <button
            type="button"
            onClick={onReimprimir}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            Imprimir comprobante
          </button>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </DrawerShell>
    )
  }

  // Vista form
  return (
    <DrawerShell onClose={onClose} title={tipoSel ? tipoSel.label : 'Nueva operación'}>
      {loadingCat ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
      ) : errorCat ? (
        <div style={{ padding: 24 }}>
          <div className="error-banner">{errorCat}</div>
        </div>
      ) : !tipoSel ? (
        // PASO 1: selector de tipo
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
          <p style={{ fontSize: 12, color: 'var(--muted-foreground)', margin: '0 0 6px', lineHeight: 1.5 }}>
            ¿Qué operación querés registrar para <strong style={{ color: 'var(--ink)' }}>{nombreCompleto}</strong>?
          </p>
          {TIPOS.map((t) => (
            <button
              key={t.codigo}
              type="button"
              onClick={() => setTipoSel(t)}
              style={{
                all: 'unset', cursor: 'pointer',
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '14px 16px',
                background: 'var(--surface)', border: '1px solid var(--hairline)',
                borderRadius: 12, transition: 'all 120ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brand)' }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--hairline)' }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: t.direccion === 'in' ? 'var(--ok-bg)' : '#fee2e2',
                color: t.direccion === 'in' ? 'var(--ok-ink)' : '#dc2626',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <t.Icon size={16} weight="duotone" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', letterSpacing: -0.2 }}>
                  {t.label}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2, lineHeight: 1.45 }}>
                  {t.hint}
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        // PASO 2: form
        <form onSubmit={onSubmit} style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1, overflow: 'auto' }}>
          {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

          <button
            type="button"
            onClick={() => setTipoSel(null)}
            style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--brand)' }}
          >
            <CaretLeft size={11} weight="bold" /> Cambiar tipo
          </button>

          {/* Cuenta destino */}
          <Field label={tipoSel.codigo === 'S4' ? 'Cuenta de la cual retira' : 'Cuenta destino'}>
            {cuentasDisponibles.length === 0 ? (
              <div className="error-banner" style={{ margin: 0, fontSize: 12 }}>
                No hay cuentas activas para esta operación.
              </div>
            ) : cuentasDisponibles.length === 1 ? (
              <div style={{
                padding: '10px 12px',
                background: 'var(--soft-gray)', border: '1px solid var(--hairline)',
                borderRadius: 8, fontSize: 13,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontWeight: 600 }}>{cuentasDisponibles[0].label}</span>
                <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>
                  Saldo Q {cuentasDisponibles[0].saldo.toFixed(2)}
                </span>
              </div>
            ) : (
              <select value={cuentaDestinoId} onChange={(e) => setCuentaDestinoId(e.target.value)} required>
                <option value="">— Elegir cuenta —</option>
                {cuentasDisponibles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label} (saldo Q {c.saldo.toFixed(2)})
                  </option>
                ))}
              </select>
            )}
          </Field>

          {/* Monto */}
          <Field label="Monto">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16, color: 'var(--muted-foreground)' }}>Q</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={montoStr}
                onChange={(e) => setMontoStr(e.target.value)}
                required
                autoFocus
                placeholder="0.00"
                style={{ flex: 1, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-mono)' }}
              />
            </div>
            {tipoSel.codigo === 'S4' && cuentaSel && (
              <span style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 4 }}>
                Saldo disponible: Q {cuentaSel.saldo.toFixed(2)}
              </span>
            )}
          </Field>

          {/* Forma de pago — depósitos: efectivo / depósito al banco propio */}
          {tipoSel.direccion === 'in' && (
            <div>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Forma de pago</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <FormaTab
                  Icon={Cash}
                  label="Efectivo"
                  hint="Dinero en caja"
                  selected={formaPago === 'efectivo'}
                  onClick={() => setFormaPago('efectivo')}
                />
                <FormaTab
                  Icon={Bank}
                  label="Depósito bancario"
                  hint="Boleta del banco"
                  selected={formaPago === 'deposito'}
                  onClick={() => setFormaPago('deposito')}
                />
              </div>
            </div>
          )}

          {/* Forma de entrega — retiros: efectivo / transferencia bancaria al asociado */}
          {tipoSel.direccion === 'out' && (
            <div>
              <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Cómo entregar el dinero</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <FormaTab
                  Icon={Cash}
                  label="Efectivo"
                  hint="El asociado retira en caja"
                  selected={formaPago === 'efectivo'}
                  onClick={() => setFormaPago('efectivo')}
                />
                <FormaTab
                  Icon={ArrowsLeftRight}
                  label="Transferencia bancaria"
                  hint={referenciaBancaria
                    ? `${referenciaBancaria.banco} · ${referenciaBancaria.numero_cuenta}`
                    : 'Sin cuenta registrada'}
                  selected={formaPago === 'transferencia_bancaria'}
                  onClick={() => setFormaPago('transferencia_bancaria')}
                  disabled={!referenciaBancaria}
                />
              </div>
              {!referenciaBancaria && formaPago === 'transferencia_bancaria' && (
                <div
                  style={{
                    marginTop: 10, padding: '10px 12px',
                    background: '#fff7ed', border: '1px solid #fed7aa',
                    borderRadius: 8, fontSize: 12, color: '#9a3412', lineHeight: 1.5,
                  }}
                >
                  El asociado todavía no tiene cuenta bancaria registrada para recibir
                  transferencias. Capturala desde el tab <strong>Cumplimiento</strong> de su
                  ficha antes de continuar.
                </div>
              )}
              {referenciaBancaria && formaPago === 'transferencia_bancaria' && (
                <div
                  style={{
                    marginTop: 10, padding: '10px 12px',
                    background: 'var(--soft-gray)', border: '1px solid var(--hairline)',
                    borderRadius: 8, fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.55,
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    Transferir a
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                    {referenciaBancaria.banco}
                  </div>
                  <div style={{ marginTop: 2, fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)' }}>
                    {referenciaBancaria.numero_cuenta} · {referenciaBancaria.tipo.toLowerCase()}
                  </div>
                  <div style={{ marginTop: 2 }}>
                    A nombre de: <strong>{referenciaBancaria.titular_nombre}</strong>
                  </div>
                  {!referenciaBancaria.validada && (
                    <div style={{ marginTop: 6, color: '#9a3412', fontSize: 11 }}>
                      ⚠ Esta cuenta no está validada todavía.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sub-form depósito */}
          {tipoSel.direccion === 'in' && formaPago === 'deposito' && (
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--hairline)',
              borderRadius: 10, padding: '12px 14px',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <Field label="Cuenta destino (banco)">
                <select value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value)} required>
                  <option value="">— Elegir —</option>
                  {cuentasBanco.map((c) => (
                    <option key={c.id} value={c.id}>{c.alias ?? c.banco} · {c.numero_cuenta}</option>
                  ))}
                </select>
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
                <Field label="Fecha boleta">
                  <input type="date" value={fechaBoleta} onChange={(e) => setFechaBoleta(e.target.value)} />
                </Field>
                <Field label="N° boleta">
                  <input value={numeroBoleta} onChange={(e) => setNumeroBoleta(e.target.value)} placeholder="9335368" />
                </Field>
                <Field label="Monto total boleta">
                  <input
                    type="number" min={monto || 0} step="0.01"
                    value={montoBoletaTotal}
                    onChange={(e) => setMontoBoletaTotal(e.target.value)}
                    placeholder={monto.toFixed(2)}
                  />
                </Field>
              </div>
            </div>
          )}

          {/* Descripción opcional */}
          <Field label="Descripción (opcional)">
            <input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej. Depósito de pago de planilla"
            />
          </Field>
        </form>
      )}

      {tipoSel && !success && (
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={(e) => onSubmit(e as unknown as FormEvent)}
            className="btn btn-primary"
            disabled={submitting || monto <= 0}
          >
            {submitting ? 'Registrando…' : `Registrar · ${formatQ(monto)}`}
          </button>
        </div>
      )}
    </DrawerShell>
  )
}

// ============================================================
// Componentes internos
// ============================================================

// DrawerShell extraído a components/DrawerShell.tsx — se reusa entre drawers.

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="form-label" style={{ fontSize: 11 }}>{label}</span>
      {children}
    </label>
  )
}

function FormaTab({
  Icon, label, hint, selected, onClick, disabled,
}: {
  Icon: typeof Cash
  label: string
  hint: string
  selected: boolean
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      disabled={disabled}
      style={{
        all: 'unset',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
        padding: '12px 14px',
        background: selected ? 'var(--brand-50)' : 'var(--surface)',
        border: `1.5px solid ${selected ? 'var(--brand)' : 'var(--hairline)'}`,
        borderRadius: 10,
        transition: 'all 120ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 24, height: 24, borderRadius: 6,
          background: selected ? 'var(--brand)' : 'var(--soft-gray)',
          color: selected ? '#fff' : 'var(--muted-foreground)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={12} weight="duotone" />
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: selected ? 'var(--brand-700)' : 'var(--ink)' }}>
          {label}
        </span>
      </div>
      <span style={{ fontSize: 10, color: 'var(--muted-foreground)', lineHeight: 1.3 }}>{hint}</span>
    </button>
  )
}

function RowMini({ label, value, bold, accent }: { label: string; value: string; bold?: boolean; accent?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ color: 'var(--muted-foreground)' }}>{label}</span>
      <span style={{
        fontWeight: bold ? 700 : 600,
        color: accent ?? 'var(--ink)',
        fontFeatureSettings: '"tnum"',
      }}>
        {value}
      </span>
    </div>
  )
}
